// Subtle per-object emissive animation.
//
// Goals:
//   - Lights pulse softly per-object (so a row of server racks don't all blink
//     in lockstep).
//   - Occasional rapid flicker — that "live data centre" feel — but rare
//     enough to be subtle, never distracting.
//   - Cheap: a single uniform per affected material, ticked on the render
//     loop. No new draw calls.
//
// How it works:
//   - On instance mount, walk the cloned scene graph and find meshes whose
//     material name === "mat-emissive". Clone the material per-instance so
//     animations don't bleed across objects, and register it with a phase
//     offset derived from the object id.
//   - On each tick, set `emissiveIntensity` to a function of time + phase.
//     Base term: a slow sine wave (period ~6s) so most lights are nearly
//     static but always alive. Flicker term: a hash-driven brief drop every
//     ~3-7 seconds.

import * as THREE from "three";

type BlinkEntry = {
  material: THREE.MeshStandardMaterial;
  /** 0..1 deterministic phase offset (so neighbours don't pulse together). */
  phase: number;
  /** Random number in [0, 1) used to skew flicker timings per material. */
  jitter: number;
  /** Baseline emissive intensity before modulation. Mat-emissive ships at
   * 1.2 from `matFor`, but we read it back here in case the look spec
   * changes. */
  baseIntensity: number;
};

const registry: BlinkEntry[] = [];

function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return h;
}

/**
 * Walk `instance` and clone every `mat-emissive` material to per-mesh,
 * registering each with a deterministic phase derived from `seedId` so
 * subsequent instances of the same kind don't blink in unison.
 *
 * Idempotent — calling again on the same instance is fine; we only register
 * materials we haven't seen yet (tag via userData).
 */
export function registerBlinkInstance(instance: THREE.Object3D, seedId: string): void {
  const baseHash = hashStr(seedId);
  let counter = 0;
  instance.traverse((c) => {
    const mesh = c as THREE.Mesh;
    if (!mesh.isMesh || !mesh.material) return;
    const arr = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    const next = arr.map((mat) => {
      const std = mat as THREE.MeshStandardMaterial;
      const name = (std.name || "").trim().replace(/\.\d+$/, "");
      if (name !== "mat-emissive") return mat;
      // Clone per-mesh so this material's emissiveIntensity can drift
      // independently. We tag with userData so a second pass doesn't
      // re-register.
      if ((std as THREE.MeshStandardMaterial & { userData?: { blinkRegistered?: boolean } }).userData?.blinkRegistered) {
        return std;
      }
      const cloned = std.clone();
      cloned.userData = { ...(cloned.userData || {}), blinkRegistered: true };
      const phase = ((baseHash * 9301 + 49297 * counter) % 233280) / 233280;
      const jitter = (((baseHash ^ counter) * 73 + 19) % 1009) / 1009;
      counter += 1;
      registry.push({
        material: cloned,
        phase,
        jitter,
        baseIntensity: cloned.emissiveIntensity,
      });
      return cloned;
    });
    mesh.material = (next.length === 1 ? next[0] : next) as THREE.Material | THREE.Material[];
  });
}

/**
 * Drop blink entries whose material is no longer attached to anything in the
 * scene. Cheap-to-call from the world-sync teardown branch — we look at the
 * `users` count via three's internal disposal hook is awkward, so we instead
 * key off whether the material's `parent` reference (via __parent userData)
 * is still in the scene. For now we simply prune by a small registry cap.
 */
export function trimBlinkRegistry(maxEntries = 1024): void {
  if (registry.length <= maxEntries) return;
  registry.splice(0, registry.length - maxEntries);
}

/**
 * Tick all registered emissive materials. Call once per frame after
 * camera/world updates and before render.
 *
 *   t          time in seconds
 *   strength   0..1 — overall amplitude. 0 => no animation, 1 => full
 *              modulation. Default 0.35 keeps the effect subtle.
 */
export function tickBlink(t: number, strength = 0.35): void {
  for (const e of registry) {
    // Baseline pulse: slow sine wave, period ~6s, magnitude 0.12.
    const slow = 0.12 * Math.sin((t * (2 * Math.PI)) / 6 + e.phase * Math.PI * 2);
    // Occasional flicker: every (3 + jitter*4) seconds, a brief 0.6s dip
    // in intensity. Modeled as a smoothstep window on a phased mod.
    const flickPeriod = 3 + e.jitter * 4;
    const flickPhase = (t + e.jitter * flickPeriod) % flickPeriod;
    let flicker = 0;
    if (flickPhase < 0.6) {
      // smooth dip: 0 -> 0.5 -> 0 over the 0.6s window
      const u = flickPhase / 0.6;
      flicker = -0.5 * Math.sin(u * Math.PI);
    }
    const factor = 1 + strength * (slow + flicker);
    e.material.emissiveIntensity = e.baseIntensity * factor;
  }
}
